import {
  useContext,
  createContext,
  ReactNode,
  useEffect,
  useState,
  useMemo
} from 'react';
import { POSSIBLE_F2 } from './constants';

// #region Types
type UaitsPageResponse = {
  id: number;
  eventId: string;

  heroSection: HeroCardType;
  statsCard: StatCardType[];
  statsSectionTitle: string;

  featureCard: FeatureCardType[];
  helpSection: HelpCardType;

  faqCard: FaqCardType[];
  testSeriesSectionTitle: string;
  customSkuCard?: CustomSKUCard[];
  hybridSkuCard?: HybridSKUCard[];
  faqSectionTitle: string;
  faqSectionSubtitle: string;

  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
};

export type HeroCardType = {
  mainHeadline: string;
  supportingText: string;
  ctaButtonLabel: string;
  tagline: string;
};

type StatCardType = {
  stats: string;
  supportingText?: string;
  title: string;
  icon?: Media;
};

type FeatureCardType = {
  title: string;
  description: string;
  tag?: string;
  image: Media;
};

type HelpCardType = {
  title: string;
  description: string;
  image?: Media;
  ctaText: string;
  contact: string;
};

type FaqCardType = {
  question: string;
  answer: string;
};

export type CustomSKUCard = {
  id: string;
  f1: string;
  f2: string;

  title: string;
  skuTag: {
    id: number;
    skuTag: string;
  }[];

  gridImage: Media;
  strikePrice: string;
  discountPercent: string;
  skuOriginalPrice: string;
  recommended: boolean;

  schedulePdf: Media;
  skuLink: string;

  keyFeature: KeyFeaturePoint[];
};

export type HybridSKUCard = {
  id: string;
  f1: string;
  f2: string;

  title: string;
  skuTag: {
    id: number;
    skuTag: string;
  }[];

  gridImage: Media;
  strikePrice: string;
  discountPercent: string;
  skuOriginalPrice: string;
  recommended: boolean;
  csvDetails: CenterSelectionData[];
  schedulePdf: Media;
  hybridDetailsCsv: {
    addressCsv: Media;
  };
  keyFeature: KeyFeaturePoint[];
};

type KeyFeaturePoint = {
  keyFeature: string;
  keyFeatureIcon?: Media;
};

type Media = {
  id: number;
  name: string;
  url: string;
  alternativeText?: string;
  caption?: string;
  mime?: string;
  width?: number;
  height?: number;
  size?: number;
  formats?: {
    [key: string]: {
      url: string;
      width: number;
      height: number;
    };
  };
};

type TestSeriesContextType = {
  data: UaitsPageResponse | null;
  showCenterSelectionModal: {
    show: boolean;
    data?: HybridSKUCard | null;
  };
  setShowCenterSelectionModal: (val: {
    show: boolean;
    data?: HybridSKUCard | null;
  }) => void;
  selectedF1: string;
  setSelectedF1: (val: string) => void;
  selectedF2: string;
  setSelectedF2: (val: string) => void;
  allPossibleF1: string[];
  allPossibleF2: string[];
  filteredCardsToShow: (CustomSKUCard | HybridSKUCard)[];
  testSeriesSectionTitle: string;
};
// #endregion
export type CenterSelectionData = {
  city: string;
  state: string;
  sku_url: string;
  test_center_name: string;
  test_centre_address: string;
};
const TestSeriesDataContext = createContext<TestSeriesContextType | null>(null);

export const useTestSeriesData = () => useContext(TestSeriesDataContext);

// #region Helper Functions

// #endregion

export const TestSeriesDataProvider = ({
  children,
  initialData
}: {
  children: ReactNode;
  initialData: UaitsPageResponse;
}) => {
  const [testSeriesData, setTestSeriesData] =
    useState<UaitsPageResponse | null>(initialData);
  const [selectedF1, setSelectedF1] = useState('');
  const [selectedF2, setSelectedF2] = useState('');

  const [showCenterSelectionModal, setShowCenterSelectionModal] = useState<{
    show: boolean;
    data?: HybridSKUCard | null;
  }>({
    show: false
  });

  const allPossibleF1 = useMemo(() => {
    if (!testSeriesData) return [];
    const customCards = testSeriesData?.customSkuCard || [];
    const hybridCards = testSeriesData?.hybridSkuCard || [];
    return Array.from(
      new Set([...customCards, ...hybridCards].map((item) => item.f1))
    );
  }, [testSeriesData]);

  const allPossibleF2 = useMemo(() => {
    if (!testSeriesData) return [];
    const customCards = testSeriesData?.customSkuCard || [];
    const hybridCards = testSeriesData?.hybridSkuCard || [];
    const allF2 = Array.from(
      new Set([...customCards, ...hybridCards].map((item) => item.f2))
    );
    // Return f2 present for selectedF1 for both custom and hybrid cards
    const filteredF2 = allF2.filter((item) =>
      [...customCards, ...hybridCards].some(
        (card) => card.f1 === selectedF1 && card.f2 === item
      )
    );
    const sortedF2 = filteredF2.sort(
      (a, b) => POSSIBLE_F2.indexOf(a) - POSSIBLE_F2.indexOf(b)
    );
    return sortedF2;
  }, [testSeriesData, selectedF1]);

  const testSeriesSectionTitle = useMemo(
    () => testSeriesData?.testSeriesSectionTitle || '',
    [testSeriesData]
  );

  const filteredCardsToShow = useMemo(() => {
    if (!testSeriesData) return [];
    const customCards = testSeriesData?.customSkuCard || [];
    const hybridCards = testSeriesData?.hybridSkuCard || [];
    return [...hybridCards, ...customCards].filter(
      (item) => item.f1 === selectedF1 && item.f2 === selectedF2
    );
  }, [testSeriesData, selectedF1, selectedF2]);

  useEffect(() => {
    if (!testSeriesData) return;
    const customCards = testSeriesData?.customSkuCard || [];
    const hybridCards = testSeriesData?.hybridSkuCard || [];
    const cards = [...hybridCards, ...customCards];

    if (cards.length > 0) {
      setSelectedF1(cards[0]?.f1 || '');
      setSelectedF2(cards[0]?.f2 || '');
    }
  }, [testSeriesData]);

  return (
    <TestSeriesDataContext.Provider
      value={{
        data: testSeriesData,
        showCenterSelectionModal,
        setShowCenterSelectionModal,
        selectedF1,
        setSelectedF1,
        selectedF2,
        setSelectedF2,
        allPossibleF1,
        allPossibleF2,
        filteredCardsToShow,
        testSeriesSectionTitle
      }}
    >
      {children}
    </TestSeriesDataContext.Provider>
  );
};
